<template>
  <div
    :class="skeletonClasses"
    :style="skeletonStyles"
    aria-hidden="true"
  />
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // Shape of the skeleton
  shape: {
    type: String,
    default: 'rectangle',
    validator: value => ['rectangle', 'square', 'circle'].includes(value)
  },
  // Size of the skeleton, only applies when shape is "square" or "circle"
  size: {
    type: [String, Number],
    default: null
  },
  // Width of the skeleton
  width: {
    type: [String, Number],
    default: '100%'
  },
  // Height of the skeleton
  height: {
    type: [String, Number],
    default: '1rem'
  },
  // Border radius of the skeleton
  borderRadius: {
    type: [String, Number],
    default: null
  },
  // Animation of the skeleton
  animation: {
    type: String,
    default: 'wave',
    validator: value => ['pulse', 'wave', 'none'].includes(value)
  }
})

const skeletonClasses = computed(() => {
  const classes = ['skeleton']

  // Add shape classes
  classes.push(`skeleton--${props.shape}`)

  // Add animation classes
  if (props.animation !== 'none') {
    classes.push(`skeleton--${props.animation}`)
  }

  return classes
})

const skeletonStyles = computed(() => {
  const styles = {}

  // Handle size for square and circle shapes
  if (props.size && (props.shape === 'square' || props.shape === 'circle')) {
    const sizeValue = typeof props.size === 'number' ? `${props.size}px` : props.size
    styles.width = sizeValue
    styles.height = sizeValue
  } else {
    // Handle width and height for rectangle shape or when size is not provided
    if (props.width !== null) {
      styles.width = typeof props.width === 'number' ? `${props.width}px` : props.width
    }
    if (props.height !== null) {
      styles.height = typeof props.height === 'number' ? `${props.height}px` : props.height
    }
  }

  // Handle border radius
  if (props.borderRadius !== null) {
    styles.borderRadius = typeof props.borderRadius === 'number' ? `${props.borderRadius}px` : props.borderRadius
  } else if (props.shape === 'circle') {
    styles.borderRadius = '50%'
  } else if (props.shape === 'square') {
    styles.borderRadius = 'var(--rounded-lg)'
  } else {
    styles.borderRadius = 'var(--rounded-lg)'
  }

  return styles
})
</script>

<style scoped>
.skeleton {
  @apply overflow-hidden relative;
  background-color: var(--bg-tertiary);
}

.dark .skeleton {
  background-color: var(--bg-secondary);
}

.skeleton--pulse {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton--wave {
  position: relative;
  overflow: hidden;
}

.skeleton--wave::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, var(--bg-primary), transparent);
  animation: skeleton-wave 2s infinite;
}

.dark .skeleton--wave::before {
  background: linear-gradient(90deg, transparent, var(--bg-tertiary), transparent);
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

@keyframes skeleton-wave {
  0% {
    left: -100%;
  }

  100% {
    left: 100%;
  }
}

/* Dark mode support - .dark class on body/root */
:global(.dark) .skeleton {
  @apply bg-gray-700;
}

/* Fallback for system dark mode preference when no .dark class is used */
@media (prefers-color-scheme: dark) {
  :global(body:not(.dark)) .skeleton {
    @apply bg-gray-700;
  }
}
</style>
